"use client";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { useTranslations } from "next-intl";
import { type FormEvent, useState } from "react";
import { z } from "zod";

interface LoginStepProps {
  eventId: string;
  locale: string;
  /** Called once login succeeds and the session cookie is set. */
  onAuthenticated: () => void;
  /** Called on a 2fa_required login (SMS already sent), with the redacted phone. */
  onTwoFactorRequired: (phoneNumber: string | null) => void;
  /** Switch to the create-account step. */
  onCreateAccount: () => void;
  /** Optional: start the password-reset sub-flow. */
  onForgotPassword?: () => void;
}

const emailSchema = z.email();

interface LoginFieldErrors {
  email?: string;
  password?: string;
}

function validateLogin(
  email: string,
  password: string,
  t: ReturnType<typeof useTranslations>,
): LoginFieldErrors {
  const errors: LoginFieldErrors = {};
  if (!emailSchema.safeParse(email).success) {
    errors.email = t("emailInvalid");
  }
  if (password.length === 0) {
    errors.password = t("passwordRequired");
  }
  return errors;
}

/**
 * Login step of the signup funnel (design `14510:4694`). Posts credentials to
 * the server-side proxy which stores the JWTs in HTTP-only cookies — tokens are
 * never handled in client JS. Errors are shown as a single normalized message
 * (the route does not reveal whether the account exists).
 */
export default function LoginStep({
  eventId,
  locale,
  onAuthenticated,
  onTwoFactorRequired,
  onCreateAccount,
  onForgotPassword,
}: LoginStepProps) {
  const t = useTranslations("publicEventSignup");
  const basePath = process.env.NEXT_PUBLIC_BASE_PATH ?? "";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fieldErrors, setFieldErrors] = useState<LoginFieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setFormError(null);

    const errors = validateLogin(email, password, t);
    setFieldErrors(errors);
    if (errors.email || errors.password) return;

    setSubmitting(true);
    try {
      const res = await fetch(
        `${basePath}/api/events/${eventId}/signup/login`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, password, locale }),
        },
      );
      const data = (await res.json().catch(() => ({}))) as {
        status?: string;
        phoneNumber?: string | null;
      };

      if (res.ok && data.status === "ok") {
        onAuthenticated();
        return;
      }
      if (res.ok && data.status === "2fa_required") {
        onTwoFactorRequired(data.phoneNumber ?? null);
        return;
      }
      setFormError(t("loginError"));
    } catch {
      setFormError(t("loginError"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="flex h-full flex-col gap-4"
      noValidate
    >
      <div className="flex flex-col gap-1">
        <h2 className="text-lg font-semibold">{t("loginHeader")}</h2>
        <p className="text-sm text-muted-foreground">{t("loginDescription")}</p>
      </div>

      <Separator />

      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="signup-login-email">{t("emailLabel")}</Label>
          <Input
            id="signup-login-email"
            type="email"
            autoComplete="email"
            placeholder={t("emailPlaceholder")}
            value={email}
            aria-invalid={!!fieldErrors.email}
            aria-describedby={
              fieldErrors.email ? "signup-login-email-error" : undefined
            }
            onChange={(e) => setEmail(e.target.value)}
          />
          {fieldErrors.email && (
            <p
              id="signup-login-email-error"
              className="text-sm text-destructive"
            >
              {fieldErrors.email}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="signup-login-password">{t("passwordLabel")}</Label>
          <Input
            id="signup-login-password"
            type="password"
            autoComplete="current-password"
            value={password}
            aria-invalid={!!fieldErrors.password}
            aria-describedby={
              fieldErrors.password ? "signup-login-password-error" : undefined
            }
            onChange={(e) => setPassword(e.target.value)}
          />
          {fieldErrors.password && (
            <p
              id="signup-login-password-error"
              className="text-sm text-destructive"
            >
              {fieldErrors.password}
            </p>
          )}
        </div>

        <button
          type="button"
          onClick={onForgotPassword}
          className="self-start text-sm font-medium text-foreground underline"
        >
          {t("forgotPassword")}
        </button>
      </div>

      {/* CTAs pinned to the sheet bottom (design 14510:4694). */}
      <div className="mt-auto flex flex-col gap-3 pt-4">
        {formError && (
          <p className="text-sm text-destructive" role="alert">
            {formError}
          </p>
        )}

        <Button
          type="submit"
          variant="default"
          disabled={submitting}
          className="w-full rounded-full"
        >
          {t("login")}
        </Button>

        <Button
          type="button"
          variant="secondary"
          onClick={onCreateAccount}
          className="w-full rounded-full"
        >
          {t("createAccount")}
        </Button>
      </div>
    </form>
  );
}
